import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { darkTheme, FONT_MONO } from '../../theme/tokens';
import { scrubAssistantText } from './scrubAssistantText';

const t = darkTheme;

// Defense-in-depth: react-markdown's `defaultUrlTransform` already strips
// non-allowlisted protocols, but we filter again here so a future framework
// default change or custom `urlTransform` can't silently re-open the hole.
// The threat surface is real: catalog field values can carry attacker-supplied
// `javascript:` links that the LLM may faithfully reproduce in its reply.
const DANGEROUS_PROTOCOL = /^\s*(javascript|data|vbscript):/i;
function safeHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  return DANGEROUS_PROTOCOL.test(href) ? undefined : href;
}

/**
 * Markdown renderer scoped to the chat assistant bubble. Custom element
 * styles match the bubble's body type. Headings render as bold body text
 * (full-size h1/h2 are visually disruptive inside chat bubbles).
 *
 * Plugins:
 *  - remark-gfm: tables, strikethrough, autolinks, task lists.
 *  - remark-breaks: convert single newlines to <br> (LLMs often emit single
 *    newlines for readability rather than full paragraph breaks).
 */
export function MarkdownText({ text }: { text: string }) {
  return (
    <div data-testid="markdown-text" style={{ fontSize: 14, lineHeight: 1.55, color: t.text }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ children }) => (
            <p style={{ margin: '0 0 8px 0' }}>{children}</p>
          ),
          strong: ({ children }) => (
            <strong style={{ fontWeight: 600 }}>{children}</strong>
          ),
          em: ({ children }) => <em>{children}</em>,
          code: ({ className, children }) => {
            // Fenced (block) code gets `language-xxx` via the markdown parser;
            // its wrapping <pre> handles padding/background. Inline code is
            // styled as a "system entity" chip — mint-tinted bg matches the
            // brand's reference-highlight pattern (cf. athl_ekom_demo.html
            // .athl-ref). Surfaces tagged IDs, field names, and other
            // technical identifiers as distinct from prose.
            const isBlock =
              typeof className === 'string' && className.startsWith('language-');
            if (isBlock) {
              return (
                <code className={className} style={{ fontFamily: FONT_MONO, fontSize: 13 }}>
                  {children}
                </code>
              );
            }
            return (
              <code
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 13,
                  fontWeight: 500,
                  background: t.accentBg,
                  color: t.accent,
                  padding: '1px 5px',
                  borderRadius: 2,
                }}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre
              style={{
                fontFamily: FONT_MONO,
                fontSize: 13,
                background: t.bgAlt,
                border: `1px solid ${t.border}`,
                borderRadius: 6,
                padding: 10,
                margin: '6px 0',
                overflowX: 'auto',
                lineHeight: 1.4,
              }}
            >
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '4px 0 8px 0', paddingLeft: 20 }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '4px 0 8px 0', paddingLeft: 20 }}>{children}</ol>
          ),
          li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
          a: ({ href, children }) => (
            <a
              href={safeHref(href)}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: t.blueText, textDecoration: 'underline' }}
            >
              {children}
            </a>
          ),
          // GFM tables. Structural styling is inline; the zebra stripe on even
          // body rows needs :nth-child, so it lives in index.css (.ekom-md-table).
          // The wrapper carries extra bottom margin so following prose isn't
          // crammed against the last row, plus overflow-x for wide tables.
          // th/td forward react-markdown's per-cell `style.textAlign` so GFM
          // column-alignment markers (:---:, ---:) are honored — e.g. right-
          // aligned numeric columns in catalog/pricing tables.
          table: ({ children }) => (
            <div style={{ overflowX: 'auto', margin: '8px 0 14px' }}>
              <table
                className="ekom-md-table"
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children, style }) => (
            <th
              scope="col"
              style={{
                textAlign: style?.textAlign ?? 'left',
                fontWeight: 600,
                padding: '6px 12px',
                borderBottom: `2px solid ${t.border2}`,
                whiteSpace: 'nowrap',
              }}
            >
              {children}
            </th>
          ),
          td: ({ children, style }) => (
            <td
              style={{
                padding: '6px 12px',
                borderBottom: `1px solid ${t.border}`,
                verticalAlign: 'top',
                ...(style?.textAlign ? { textAlign: style.textAlign } : {}),
              }}
            >
              {children}
            </td>
          ),
          h1: ({ children }) => (
            <div style={{ fontWeight: 700, fontSize: 15, margin: '8px 0 4px' }}>
              {children}
            </div>
          ),
          h2: ({ children }) => (
            <div style={{ fontWeight: 700, fontSize: 15, margin: '8px 0 4px' }}>
              {children}
            </div>
          ),
          h3: ({ children }) => (
            <div style={{ fontWeight: 600, fontSize: 14, margin: '6px 0 2px' }}>
              {children}
            </div>
          ),
          h4: ({ children }) => (
            <div style={{ fontWeight: 600, fontSize: 14, margin: '6px 0 2px' }}>
              {children}
            </div>
          ),
          h5: ({ children }) => (
            <div style={{ fontWeight: 600, fontSize: 14, margin: '6px 0 2px' }}>
              {children}
            </div>
          ),
          h6: ({ children }) => (
            <div style={{ fontWeight: 600, fontSize: 14, margin: '6px 0 2px' }}>
              {children}
            </div>
          ),
        }}
      >
        {scrubAssistantText(text)}
      </ReactMarkdown>
    </div>
  );
}
